import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const FALLBACK = { primaryColor:'#4f46e5', secondaryColor:'#0891b2', accentColor:'#d97706' };

const brandedFallbackDataUri = (name = 'Cold Storage', primary = FALLBACK.primaryColor, secondary = FALLBACK.secondaryColor) => {
  const initials = String(name || 'Cold Storage').trim().split(/\s+/).filter(Boolean).slice(0,2).map((part)=>part[0]?.toUpperCase() || '').join('') || 'CS';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 180 180"><defs><linearGradient id="g" x1="0" x2="1"><stop stop-color="${primary}"/><stop offset="1" stop-color="${secondary}"/></linearGradient></defs><rect width="180" height="180" rx="42" fill="url(#g)"/><path d="M90 36v108M43 63l94 54M43 117l94-54" stroke="rgba(255,255,255,.26)" stroke-width="8" stroke-linecap="round"/><text x="90" y="105" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="48" font-weight="900" fill="white">${initials}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// One branded logo loader is allowed on screen at a time. Several routed pages
// still contain legacy/nested loading branches; without a singleton guard they
// can render two or three company logos simultaneously. The registry promotes
// the next mounted loader automatically when the current owner unmounts.
let loaderSeed = 0;
const activeLoaderIds = [];
const loaderListeners = new Set();
const notifyLoaderOwners = () => loaderListeners.forEach((listener) => {
  try { listener(); } catch (_) {}
});
const registerLoader = (id) => {
  if (!activeLoaderIds.includes(id)) activeLoaderIds.push(id);
  notifyLoaderOwners();
};
const unregisterLoader = (id) => {
  const index = activeLoaderIds.indexOf(id);
  if (index >= 0) activeLoaderIds.splice(index, 1);
  notifyLoaderOwners();
};

export default function CompanyShatterLoader({ label = 'Loading…', compact = false, overlay = false }) {
  const themeContext = useTheme();
  const idRef = React.useRef('');
  if (!idRef.current) idRef.current = `company-loader-${++loaderSeed}`;
  const [isOwner, setIsOwner] = React.useState(false);

  React.useEffect(() => {
    const id = idRef.current;
    const sync = () => setIsOwner(activeLoaderIds[0] === id);
    loaderListeners.add(sync);
    registerLoader(id);
    sync();
    return () => {
      loaderListeners.delete(sync);
      unregisterLoader(id);
    };
  }, []);

  const primaryColor = themeContext?.branding?.primaryColor || FALLBACK.primaryColor;
  const secondaryColor = themeContext?.branding?.secondaryColor || FALLBACK.secondaryColor;
  const generatedFallback = brandedFallbackDataUri(themeContext?.companyName || 'Cold Storage', primaryColor, secondaryColor);
  const splashLogo = themeContext?.branding?.splashLogoUrl || '';
  const visual = {
    logo: splashLogo || themeContext?.companyLogo || generatedFallback,
    fallbackLogo: generatedFallback,
    primaryColor,
    secondaryColor,
    accentColor: themeContext?.branding?.accentColor || FALLBACK.accentColor
  };

  // Nested loaders deliberately render nothing while another branded loader is
  // active. This prevents duplicate logos without covering already-fetched data.
  if (!isOwner) return null;

  const positions = [
    ['0%','0%'], ['50%','0%'], ['100%','0%'],
    ['0%','50%'], ['50%','50%'], ['100%','50%'],
    ['0%','100%'], ['50%','100%'], ['100%','100%']
  ];

  const splashBackground = themeContext?.branding?.splashBackgroundUrl || '';
  const splashBackgroundColor = themeContext?.branding?.splashBackgroundColor || '';
  const style = {
    '--loader-primary': visual.primaryColor,
    '--loader-secondary': visual.secondaryColor,
    '--loader-accent': visual.accentColor,
    ...(overlay && splashBackground ? { backgroundImage: `linear-gradient(rgba(248,250,252,.88),rgba(248,250,252,.92)), url(${splashBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
    ...(overlay && !splashBackground && splashBackgroundColor ? { backgroundColor: splashBackgroundColor } : {}),
  };

  return (
    <div style={style} className={`company-shatter-loader${overlay ? ' is-overlay' : ''}${compact ? ' is-compact' : ''}`} role="status" aria-live="polite">
      <div className="company-shatter-orbit" aria-hidden="true">
        <img
          className="company-shatter-base-logo"
          src={visual.logo}
          alt=""
          onError={(event) => {
            if (!event.currentTarget.dataset.fallbackApplied) {
              event.currentTarget.dataset.fallbackApplied = 'true';
              event.currentTarget.src = visual.fallbackLogo;
            }
          }}
        />
        <div className="company-shatter-grid">
          {positions.map(([x,y], index) => (
            <span
              key={`${x}-${y}`}
              className="company-shatter-piece"
              style={{
                '--piece-index': index,
                backgroundImage: `url("${visual.logo}")`,
                backgroundPosition: `${x} ${y}`
              }}
            />
          ))}
        </div>
      </div>
      <div className="company-shatter-label">{label}</div>
      <div className="company-shatter-dots" aria-hidden="true"><i/><i/><i/></div>
    </div>
  );
}
